import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

const PUBLIC = process.env.PUBLIC_URL || '';

// Framed report screenshot (browser-window style). Falls back to a labeled
// placeholder until the image file is present in /public/reports.
const ReportShot = ({ src, alt, file, bg }) => {
  const [err, setErr] = useState(false);
  const tint = bg ? { background: bg } : undefined;
  return (
    <div className="hero-ss-shot" style={tint}>
      <div className="hero-ss-shot-bar" style={tint}>
        <span></span><span></span><span></span>
      </div>
      <div className="hero-ss-shot-body" style={tint}>
        {!err && (
          <img src={src} alt={alt} onError={() => setErr(true)} />
        )}
        {err && (
          <div className="hero-ss-shot-fallback">
            Add this screenshot:<br />
            <code>public/reports/{file}</code>
          </div>
        )}
      </div>
    </div>
  );
};

// Slide content --------------------------------------------------------------

const SLIDES = [
  {
    layout: 'photo',
    bg: `${PUBLIC}/avenir-team.jpg`,
    eyebrow: 'Built at MIT',
    title: (
      <><span className="hero-accent-light">AI-native</span> employee benefits intelligence, built for brokers, consultants, and employers</>
    ),
    cta: { to: '/contact', label: 'Book a Demo' },
  },
  {
    layout: 'photo',
    bg: 'https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?fm=jpg&q=70&w=2000&auto=format&fit=crop',
    credit: { name: 'Vitaly Gariev', url: 'https://unsplash.com/@silverkblack' },
    eyebrow: 'Proven results',
    title: (
      <>National benefits advisory firm cuts reporting time <span className="hero-accent-light">80%</span> with Avenir</>
    ),
    cta: { to: '/case-studies', label: 'Read the Case Study' },
  },
  {
    layout: 'photo',
    eyebrow: 'Enriched data',
    title: (
      <>Your data, enriched with <span className="gradient-text">150+ external sources</span> not in data warehouses</>
    ),
    cta: { to: '/product', label: 'See Our Data Sources' },
    bg: 'https://images.unsplash.com/photo-1758876203026-99a024dc43b9?fm=jpg&q=70&w=2000&auto=format&fit=crop',
    credit: { name: 'Vitaly Gariev', url: 'https://unsplash.com/@silverkblack' },
  },
];

const HeroSlideshow = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = SLIDES.length;
  const timer = useRef(null);

  const go = useCallback((i) => setActive(((i % count) + count) % count), [count]);
  const next = useCallback(() => setActive((a) => (a + 1) % count), [count]);
  const prev = useCallback(() => setActive((a) => (a - 1 + count) % count), [count]);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(() => setActive((a) => (a + 1) % count), 6500);
    return () => clearInterval(timer.current);
  }, [paused, count, active]);

  const slide = SLIDES[active];

  const TextBlock = (
    <div className="hero-ss-text">
      {/* eyebrow is kept in the slide data purely as a stable key and as the
          dot's accessible label; it is no longer rendered on the slide */}
      <h1 className="hero-title">{slide.title}</h1>
      {slide.description && <p className="hero-description">{slide.description}</p>}
      <div className="hero-buttons">
        {slide.cta.external ? (
          <a href={slide.cta.href} className="btn btn-primary btn-large" target="_blank" rel="noreferrer">
            {slide.cta.label}
          </a>
        ) : (
          <Link to={slide.cta.to} className="btn btn-primary btn-large">
            {slide.cta.label}
          </Link>
        )}
      </div>
    </div>
  );

  let body;
  if (slide.layout === 'photo') {
    body = (
      <div
        className="hero-ss-slide hero-ss-photo"
        key={active}
        style={{ backgroundImage: `url("${slide.bg}")` }}
      >
        <div className="hero-ss-photo-overlay"></div>
        <div className="hero-ss-photo-content">{TextBlock}</div>
        {slide.credit && (
          <span className="hero-ss-credit">
            Photo:{' '}
            <a href={slide.credit.url} target="_blank" rel="noreferrer">
              {slide.credit.name}
            </a>{' '}
            / Unsplash
          </span>
        )}
      </div>
    );
  } else {
    body = (
      <div className="hero-ss-slide" key={active}>
        {TextBlock}
        <div className="hero-ss-visual">
          <ReportShot src={slide.shot.src} alt={slide.shot.alt} file={slide.shot.file} bg={slide.shot.bg} />
        </div>
      </div>
    );
  }

  return (
    <section className="hero hero-slideshow-section">
      <div className="hero-ss">
        <div
          className="hero-ss-track"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {body}

          <div className="hero-ss-trust">
            <div className="hero-trust-pill">
              <span className="hero-trust-label">Trusted by Fortune 500s</span>
              <span className="hero-trust-label">Built at MIT</span>
              <span className="hero-trust-label">Backed by</span>
              <img className="hero-trust-mark" src="/a16z.png" alt="a16z speedrun" />
            </div>
          </div>

          <button className="hero-ss-arrow hero-ss-arrow-prev" onClick={prev} aria-label="Previous slide">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button className="hero-ss-arrow hero-ss-arrow-next" onClick={next} aria-label="Next slide">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <div className="hero-ss-dots">
            {SLIDES.map((s, i) => (
              <button
                key={s.eyebrow}
                className={`hero-ss-dot ${i === active ? 'active' : ''}`}
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSlideshow;
